/**
 * ESG SaaS Platform - Waste API Service
 *
 * API service for waste management data (GRI 306).
 */

import { api } from './api';

// Types
export type WasteCategory = 'Hazardous' | 'Non-Hazardous';
export type WasteType = 'General Waste' | 'Paper/Cardboard' | 'Plastics' | 'Metal' | 'Glass' | 'Organic/Food' | 'Textile/Fabric' | 'E-waste' | 'Chemical Waste' | 'Medical Waste' | 'Construction Debris' | 'Other';
export type DisposalMethod = 'Landfill' | 'Recycled' | 'Composting' | 'Incineration' | 'Incineration with Energy Recovery' | 'Specialized Treatment' | 'Reuse' | 'Deep Well Injection' | 'Other';
export type WasteUnit = 'kg' | 'tonnes';

export interface WasteEntryCreate {
  category: WasteCategory;
  waste_type: WasteType;
  disposal_method: DisposalMethod;
  amount: number;
  unit?: WasteUnit;
  facility_id?: string;  // Optional for organization-wide entries
  year: number;
  month: number;
  reporting_period?: string;  // Auto-generated from year/month by backend
  contractor?: string;
  waste_manifest_id?: string;
  notes?: string;
}

export interface WasteEntry {
  id: string;
  category: string;
  waste_type: string;
  disposal_method: string;
  amount: number;
  unit: string;
  facility_id: string;
  facility_name: string;
  reporting_period: string;
  contractor?: string;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

export interface WasteListResponse {
  entries: WasteEntry[];
  total: number;
  limit: number;
  offset: number;
  total_waste: number;
  recycling_rate: number;
  approved_count: number;
  pending_count: number;
}

export interface WasteSummary {
  total_waste: number;
  total_hazardous: number;
  total_non_hazardous: number;
  recycling_rate: number;
  diversion_rate: number;
  by_disposal: Record<string, number>;
  by_type: Record<string, number>;
  by_facility: Record<string, number>;
  approved_count: number;
  pending_count: number;
  completion_percentage: number;
}

// Query parameters
interface ListParams {
  period?: string;
  facility?: string;
  category?: WasteCategory;
  disposal_method?: DisposalMethod;
  status?: string;
  limit?: number;
  offset?: number;
}

// Service functions
export const wasteService = {
  // CRUD Operations
  createEntry: (entry: WasteEntryCreate) =>
    api.post<WasteEntry>('/waste/entries', entry),

  listEntries: (params: ListParams = {}) => {
    const query = new URLSearchParams();
    if (params.period) query.append('period', params.period);
    if (params.facility) query.append('facility', params.facility);
    if (params.category) query.append('category', params.category);
    if (params.disposal_method) query.append('disposal_method', params.disposal_method);
    if (params.status) query.append('status', params.status);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());
    const queryString = query.toString();
    return api.get<WasteListResponse>(`/waste/entries${queryString ? `?${queryString}` : ''}`);
  },

  getEntry: (entryId: string) =>
    api.get<WasteEntry>(`/waste/entries/${entryId}`),

  updateEntry: (entryId: string, entry: WasteEntryCreate) =>
    api.put<{ id: string; updated: boolean }>(`/waste/entries/${entryId}`, entry),

  deleteEntry: (entryId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/waste/entries/${entryId}`),

  // Approval workflow
  approveEntry: (entryId: string, comment?: string) =>
    api.post<{ id: string; status: string }>(`/waste/entries/${entryId}/approve`, { comment }),

  rejectEntry: (entryId: string, reason: string) =>
    api.post<{ id: string; status: string; reason: string }>(`/waste/entries/${entryId}/reject`, { reason }),

  // Bulk upload
  bulkUpload: async (file: File, period: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.postFormData(`/waste/bulk-upload?period=${encodeURIComponent(period)}`, formData);
  },

  // Analytics
  getSummary: (period?: string, facilityId?: string) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (facilityId) params.append('facility_id', facilityId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get<WasteSummary>(`/waste/summary${query}`);
  },

  getByDisposalMethod: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ disposal_methods: { method: string; amount: number; percentage: number }[] }>(
      `/waste/by-disposal-method${query}`
    );
  },

  getByFacility: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ facilities: { facility_id: string; name: string; total: number; hazardous: number; recycled: number; recycling_rate: number }[] }>(
      `/waste/by-facility${query}`
    );
  },

  getTrends: (startPeriod: string, endPeriod: string) =>
    api.get<{ periods: string[]; total_waste: number[]; recycled: number[]; recycling_rate: number[] }>(
      `/waste/trends?start_period=${startPeriod}&end_period=${endPeriod}`
    ),

  getHazardousSummary: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{
      total_hazardous: number;
      by_type: { type: string; amount: number; disposal: string }[];
      contractors: { name: string; amount: number; certified: boolean }[];
      compliance_status: string;
    }>(`/waste/hazardous-summary${query}`);
  },

  // Export (Admin only)
  exportData: async (format: 'csv' | 'xlsx' = 'csv', period?: string): Promise<void> => {
    const query = new URLSearchParams({ format });
    if (period) query.append('period', period);

    const response = await api.getBlob(`/waste/export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `waste_export.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};

// ================== WASTE ATTACHMENT TYPES ==================

export interface WasteAttachment {
  id: string;
  original_filename: string;
  file_size_bytes: number;
  file_type: string;
  uploaded_by: string;
  uploaded_at: string;
  waste_entry_id?: string;
}

export interface WasteAttachmentUploadResponse {
  attachments: WasteAttachment[];
  total_uploaded: number;
  total_size_bytes: number;
}

// ================== WASTE ATTACHMENT SERVICE ==================
export const wasteAttachmentService = {
  /**
   * Upload multiple files as evidence attachments for waste entries.
   * Files are stored in audit-friendly folder structure by year/month.
   *
   * Evidence types:
   * - Recycling certificates from third-party recyclers
   * - Waste manifest documents (chain of custody)
   * - Weighbridge tickets
   * - Hazardous waste disposal certificates
   */
  uploadFiles: async (
    files: File[],
    year: number,
    month: number
  ): Promise<WasteAttachment[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.postFormData<WasteAttachmentUploadResponse>(
      `/waste-attachments/upload?year=${year}&month=${month}`,
      formData
    );
    return response.attachments;
  },

  /**
   * Download a waste attachment file.
   * Admin only.
   */
  downloadAttachment: async (attachmentId: string): Promise<{ url: string; filename: string }> => {
    const response = await api.getBlob(`/waste-attachments/${attachmentId}/download`);
    const url = URL.createObjectURL(response.blob);
    return { url, filename: response.filename || 'download' };
  },

  /**
   * Delete a waste attachment.
   * Before approval: owner can delete. After approval: admin only.
   */
  deleteAttachment: (attachmentId: string) =>
    api.delete<{ deleted: boolean; id: string }>(`/waste-attachments/${attachmentId}`),

  /**
   * List all attachments for a specific waste entry.
   * Admin only.
   */
  listEntryAttachments: (entryId: string) =>
    api.get<WasteAttachment[]>(`/waste-attachments/entry/${entryId}`),

  /**
   * Link an uploaded attachment to a waste entry.
   */
  linkAttachmentToEntry: (attachmentId: string, entryId: string) =>
    api.post<WasteAttachment>(`/waste-attachments/${attachmentId}/link/${entryId}`, {}),

  /**
   * Export all waste evidence files as a ZIP archive for audit purposes.
   * Admin only. Useful for GRI 306 audit and verification.
   */
  exportAuditEvidence: async (year: number, month?: number): Promise<void> => {
    const query = new URLSearchParams({ year: year.toString() });
    if (month) query.append('month', month.toString());

    const response = await api.getBlob(`/waste-attachments/audit-export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `waste_evidence_export_${year}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Export waste data as CSV or Excel file (Admin only).
   */
  exportData: async (format: 'csv' | 'xlsx' = 'csv', period?: string): Promise<void> => {
    const query = new URLSearchParams({ format });
    if (period) query.append('period', period);

    const response = await api.getBlob(`/waste/export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `waste_export.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
